import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Api } from "api";
import { AcsEventResponse } from "api/generated";
import { ACS_EVENTS_REFRESH_INTERVAL_SECONDS } from "./acsEventsConfig";

type RequestKind = "initial" | "refresh" | "older";

export function useAcsEvents() {
  const [events, setEvents] = useState<AcsEventResponse[]>([]);
  const [pending, setPending] = useState<RequestKind | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [paused, setPaused] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<{ kind: RequestKind; message: string } | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const mounted = useRef(false);
  const request = useRef<AbortController | null>(null);
  const initialized = useRef(false);
  const newestId = useRef<number | null>(null);
  const oldestId = useRef<number | null>(null);
  const denied = useRef(false);

  const load = useCallback(async (kind: RequestKind) => {
    if (!mounted.current || request.current || denied.current) return;
    if (kind === "older" && oldestId.current === null) return;

    const controller = new AbortController();
    request.current = controller;
    setPending(kind);
    try {
      const { data } = await Api.AcsEvents.getAcsEvents(
        kind === "refresh" ? newestId.current ?? 0 : undefined,
        kind === "older" ? oldestId.current ?? undefined : undefined,
        25,
        { signal: controller.signal, timeout: 30000 }
      );
      if (!mounted.current || controller.signal.aborted) return;

      const hadHistory = oldestId.current !== null;
      if (data.newestEventId != null) {
        newestId.current = Math.max(newestId.current ?? data.newestEventId, data.newestEventId);
      }
      if (data.oldestEventId != null) {
        oldestId.current = Math.min(oldestId.current ?? data.oldestEventId, data.oldestEventId);
      }
      // A refresh's boundary must not replace the availability of already loaded history.
      if (kind !== "refresh" || !hadHistory) setHasMoreOlder(data.hasMoreOlder);
      if (data.items.length > 0) {
        setEvents((previous) => {
          const merged = new Map(previous.map((event) => [event.id, event]));
          data.items.forEach((event) => merged.set(event.id, event));
          return Array.from(merged.values()).sort((a, b) => b.id - a.id);
        });
      }
      initialized.current = true;
      setLoaded(true);
      if (kind !== "older") setLastChecked(new Date());
      setError((previous) => previous?.kind === kind ? null : previous);
    } catch (failure) {
      if (!mounted.current || controller.signal.aborted || axios.isCancel(failure)) return;
      const status = axios.isAxiosError(failure) ? failure.response?.status : undefined;
      if (status === 401 || status === 403) {
        denied.current = true;
        setAccessDenied(true);
        setEvents([]);
      }
      setError({
        kind,
        message: status === 401 ? "Your session has expired. Please sign in again."
          : status === 403 ? "You no longer have permission to view the Token events log."
          : kind === "older" ? "Could not load older events. Your loaded events are still available."
          : kind === "refresh" ? "Could not refresh events. Showing the last successful result."
          : "Could not load the Token events log. Please try again.",
      });
    } finally {
      if (request.current === controller) {
        request.current = null;
        if (mounted.current) setPending(null);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load("initial");
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (paused || accessDenied) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) void load(initialized.current ? "refresh" : "initial");
    }, ACS_EVENTS_REFRESH_INTERVAL_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [paused, accessDenied, load]);

  const togglePaused = () => {
    setPaused(!paused);
    if (paused) void load(initialized.current ? "refresh" : "initial");
  };

  return {
    events, pending, loaded, paused, hasMoreOlder, lastChecked, error, accessDenied,
    togglePaused,
    refresh: () => load(initialized.current ? "refresh" : "initial"),
    loadOlder: () => load("older"),
    retry: () => load(error?.kind ?? "initial"),
  };
}
